import { Component, Prop, h, Host } from '@stencil/core';
import { NAVBAR_SEO_LINKS } from '../type-1/seo-links';
import { SignNowLogo } from '../shared/sn-logo';

const CHEVRON_SYMBOL_ID = 'sn-navbar-placeholder-chevron';

/**
 * The chevron path, defined once per document.
 *
 * Four dropdown labels use the same glyph. Emitting the full `<svg>` four times cost
 * ~1.1 KB of first-screen HTML; one `<symbol>` plus four `<use>` references costs ~0.7 KB.
 *
 * It stays INLINE rather than becoming a `me-icon` or a CSS mask on an asset file: this
 * placeholder IS the first-screen navbar, and both alternatives put a network request in
 * the critical paint path — a pending `mask-image` renders the element fully masked out,
 * so the icons would flash invisible. A data-URI mask avoids the request but moves the
 * same bytes into the always-inlined global sheet, which is no cheaper.
 */
const ChevronSymbol = () => (
  <svg width="0" height="0" aria-hidden="true" style={{ position: 'absolute' }}>
    <symbol id={CHEVRON_SYMBOL_ID} viewBox="0 0 24 24">
      <path d="M16.293 8.29297L12 12.586L7.70697 8.29297L6.29297 9.70697L12 15.414L17.707 9.70697L16.293 8.29297Z" fill="currentColor" />
    </symbol>
  </svg>
);

/** Chevron-down icon, matching how `me-button` renders `arrows--chevron-down`. */
const ChevronDown = () => (
  <svg class="me-button__icon" width="24" height="24" aria-hidden="true">
    <use href={`#${CHEVRON_SYMBOL_ID}`} />
  </svg>
);

/** Search icon, inlined exactly as me-button inlines `interface--search`. */
const SearchIcon = () => (
  <svg class="me-button__icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M10 18C11.775 17.9996 13.4988 17.4054 14.897 16.312L19.293 20.708L20.707 19.294L16.311 14.898C17.405 13.4997 17.9996 11.7754 18 10C18 5.589 14.411 2 10 2C5.589 2 2 5.589 2 10C2 14.411 5.589 18 10 18ZM10 4C13.309 4 16 6.691 16 10C16 13.309 13.309 16 10 16C6.691 16 4 13.309 4 10C4 6.691 6.691 4 10 4Z"
      fill="currentColor"
    />
  </svg>
);

/**
 * Lightweight navbar placeholder for SSR — reserves navbar space and provides SEO-visible navigation
 * links before the full sn-navbar-type-1 loads client-side.
 *
 * Renders a self-contained STATIC bar (no navbar-type-1 / me-dropdown / me-button elements) so it is
 * cheap to paint and hydrates nothing. It reuses the global `.me-button*` classes for pixel-identical
 * buttons and its visual shell lives in the critical global sheet (see _shell.scss, imported by
 * source/global.scss). Because the component ships no stylesheet of its own, it has no styleUrl.
 *
 * @product signnow
 * @version 1.0.0
 * @category SignNow
 * @source navbar
 * @example
 *   <sn-navbar-placeholder-type-2></sn-navbar-placeholder-type-2>
 */
@Component({
  tag: 'sn-navbar-placeholder-type-2',
  shadow: false,
})
export class SnNavbarPlaceholderType2 {
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';
  @Prop({ attribute: 'logo-href' }) logoHref: string = '/';
  @Prop() showContactSales: boolean = true;
  @Prop() showFreeTrial: boolean = true;

  /** @content */
  @Prop({ attribute: 'features-label' }) featuresLabel: string = 'Features';
  /** @content */
  @Prop({ attribute: 'solutions-label' }) solutionsLabel: string = 'Solutions';
  /** @content */
  @Prop({ attribute: 'integrations-label' }) integrationsLabel: string = 'Integrations';
  /** @content */
  @Prop({ attribute: 'api-label' }) apiLabel: string = 'API';
  /** @content */
  @Prop({ attribute: 'pricing-label' }) pricingLabel: string = 'Pricing';
  @Prop({ attribute: 'pricing-href' }) pricingHref: string = 'https://snseats.signnow.com/pricing';
  /** @content */
  @Prop({ attribute: 'contact-sales-label' }) contactSalesLabel: string = 'Contact sales';
  @Prop({ attribute: 'contact-sales-href' }) contactSalesHref: string = '/contact';
  /** @content */
  @Prop({ attribute: 'free-trial-label' }) freeTrialLabel: string = 'Free trial';
  @Prop({ attribute: 'free-trial-href' }) freeTrialHref: string = 'https://snseats.signnow.com/purchase/business_free_trial/loggedout';
  /** @content */
  @Prop({ attribute: 'support-label' }) supportLabel: string = 'Support';
  @Prop({ attribute: 'support-href' }) supportHref: string = 'https://support.signnow.com';
  /** @content */
  @Prop({ attribute: 'request-demo-label' }) requestDemoLabel: string = 'Request a demo';
  @Prop({ attribute: 'request-demo-href' }) requestDemoHref: string = '/contact?utm_source=sn-header&utm_medium=contact-sales';

  /** Dropdown-trigger label (ghost button + trailing chevron), matching the real navbar. */
  private renderDropdownLabel(label: string) {
    return (
      <span class="me-button me-button--ghost me-button--icon-after">
        {label}
        <ChevronDown />
      </span>
    );
  }

  render() {
    return (
      <Host class="sn-navbar-placeholder-type-2" data-color-mode={this.colorMode}>
        <ChevronSymbol />

        {/* SEO links - visually hidden but accessible to crawlers */}
        <nav class="sn-navbar-placeholder-type-2__seo-links" aria-label="Site navigation">
          {NAVBAR_SEO_LINKS.map(link => (
            <a href={link.defaultHref}>{link.defaultText}</a>
          ))}
        </nav>

        <header class="sn-navbar-placeholder-type-2__bar">
          <a href={this.logoHref} class="sn-navbar-placeholder-type-2__logo" aria-label="SignNow - Go to homepage">
            <SignNowLogo />
          </a>

          <nav class="sn-navbar-placeholder-type-2__nav" aria-hidden="true">
            {this.renderDropdownLabel(this.featuresLabel)}
            {this.renderDropdownLabel(this.solutionsLabel)}
            {this.renderDropdownLabel(this.integrationsLabel)}
            {this.renderDropdownLabel(this.apiLabel)}
            <a href={this.pricingHref} class="me-button me-button--ghost">
              {this.pricingLabel}
            </a>
          </nav>

          <div class="sn-navbar-placeholder-type-2__actions">
            {this.showContactSales && (
              <a href={this.contactSalesHref} class="me-button me-button--secondary">
                {this.contactSalesLabel}
              </a>
            )}
            {this.showFreeTrial && (
              <a href={this.freeTrialHref} class="me-button me-button--primary">
                {this.freeTrialLabel}
              </a>
            )}
            <span class="me-button me-button--ghost me-button--icon-only" role="button" aria-label="Search">
              <SearchIcon />
            </span>
            {/*
              Login link — hidden on desktop, shown below the 960 collapse breakpoint in place
              of "Contact sales" (same target as sn-banner-type-1 / sn-navbar-type-1). Kept here
              so the placeholder is pixel-identical to the real navbar in the collapsed range and
              there is no jump on swap. Visibility/swap lives in _shell.scss.
            */}
            <a href="https://app.signnow.com" class="me-button me-button--ghost sn-navbar-placeholder-type-2__login-link">
              Login
            </a>
          </div>

          <button type="button" class="sn-navbar-placeholder-type-2__toggle" aria-label="Open menu" aria-hidden="true" tabindex="-1">
            <span></span>
            <span></span>
            <span></span>
          </button>
        </header>
      </Host>
    );
  }
}
